'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

export type MessageSoundSettings = {
  enabled: boolean;
  soundId: string;
  customSound: string | null;
};

export type MessageSoundOption = {
  id: string;
  label: string;
  url: string;
};

export const MESSAGE_SOUND_OPTIONS: MessageSoundOption[] = [
  { id: 'soft', label: 'Мягкий сигнал', url: '/sounds/new_message-di-ding.wav' },
  { id: 'bright', label: 'Яркий сигнал', url: '/sounds/new-message-sound.wav' },
  { id: 'pyk-toon', label: 'Пык тун-н-н', url: '/sounds/pyk-toon-n-n.wav' },
  { id: 'whatsapp', label: 'whatsapp', url: '/new-message-whatsapp.wav' },
];

const STORAGE_KEY = 'messageSoundSettings';

export function getDefaultMessageSoundSettings(): MessageSoundSettings {
  return {
    enabled: true,
    soundId: MESSAGE_SOUND_OPTIONS[0]?.id ?? 'soft',
    customSound: null,
  };
}

export function readMessageSoundSettings(): MessageSoundSettings {
  if (typeof window === 'undefined') return getDefaultMessageSoundSettings();

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultMessageSoundSettings();
    const parsed = JSON.parse(raw) as Partial<MessageSoundSettings>;

    const defaultSettings = getDefaultMessageSoundSettings();
    const soundId =
      parsed.soundId &&
      (parsed.soundId === 'custom' ||
        MESSAGE_SOUND_OPTIONS.some((option) => option.id === parsed.soundId))
        ? parsed.soundId
        : defaultSettings.soundId;

    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : defaultSettings.enabled,
      soundId,
      customSound: typeof parsed.customSound === 'string' ? parsed.customSound : null,
    };
  } catch {
    return getDefaultMessageSoundSettings();
  }
}

export function saveMessageSoundSettings(settings: MessageSoundSettings) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function resolveMessageSoundUrl(settings: MessageSoundSettings): string | null {
  if (!settings.enabled) return null;
  if (settings.soundId === 'custom') return settings.customSound ?? null;
  return MESSAGE_SOUND_OPTIONS.find((option) => option.id === settings.soundId)?.url ?? null;
}

export function useMessageSoundSettings() {
  const [settings, setSettings] = useState<MessageSoundSettings>(() =>
    getDefaultMessageSoundSettings(),
  );

  useEffect(() => {
    setSettings(readMessageSoundSettings());
  }, []);

  const updateSettings = useCallback((patch: Partial<MessageSoundSettings>) => {
    setSettings((prev) => {
      const next: MessageSoundSettings = {
        ...prev,
        ...patch,
      };
      saveMessageSoundSettings(next);
      return next;
    });
  }, []);

  const options = useMemo(() => MESSAGE_SOUND_OPTIONS, []);

  return { settings, updateSettings, options };
}